import { useState, useEffect, useRef } from "react";
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, IconButton, InputAdornment, InputLabel, OutlinedInput, Paper } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ref, set } from "firebase/database";
import { rtdb } from './firebase.js'
import CustomDataGrid from './CustomDataGrid.js';
import PeopleIcon from '@mui/icons-material/People';


const debitVG = (v, r) => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    return [...new Set(v || [])].toSorted().join(' - ');
};

const familyGroupVG = (value, row) => value ? (<PeopleIcon sx={{m:'-6px'}} />) : ''

const columns = [
    { field: 'checked', headerName: '🗹', width: 50, valueGetter: (value, row) => value ? "🗹" : "☐", align: 'center', },
    { field: 'socio', headerName: 'Socio', width: 70, align: 'center', headerAlign: 'center', valueGetter: (value, row) => value ? Number(value) : '' },
    { field: 'fullname', headerName: 'Nombre', width: 250, valueGetter: (value, row) => `${(row.apellido || '').trim() || ''}, ${(row.nombre || '').trim() || ''}`, },
    { field: 'grupo_familiar', headerName: 'G/F', width: '100%', align: 'center', headerAlign: 'center', valueGetter: familyGroupVG, titleGetter: (value, row) => row.familyGroup || '' },
    { field: 'matricula', headerName: 'Matricula', width: 85, headerAlign: 'center' },
    { field: 'direccion', headerName: 'Direccion', width: 150, headerAlign: 'center' },
    { field: 'ciudad', headerName: 'Ciudad', width: 120, headerAlign: 'center' },
    { field: 'telefono', headerName: 'Telefono', width: 100, headerAlign: 'center' },
    { field: 'email', headerName: 'e-mail', width: 200, headerAlign: 'center' },
    { field: 'documentonumero', headerName: 'DNI', width: 85, headerAlign: 'center' },
    { field: 'sexo', headerName: 'Sexo', width: 85, align: 'center', headerAlign: 'center' },
    { field: 'fnacimiento', headerName: 'F. Nac', width: 95, valueGetter: (value, row) => value.split('-').reverse().join('/'), align: 'center' },
    { field: 'gruposang', headerName: 'G.S.', width: 55, align: 'center', headerAlign: 'center' },
    { field: 'catsocio', headerName: 'Cat. Socio', width: 150, align: 'center', headerAlign: 'center' },
    { field: 'debitos', headerName: 'Debitos', width: 125, align: 'center', headerAlign: 'center', valueGetter: debitVG },
]

function GridHeader(props) {
    const { propsSearch, preSearch, userFilter, setPage, User, EscKeyStroke } = props
    const [search, setSearch] = useState(preSearch || '');
    const [openDialog, setOpenDialog] = useState(false);
    const lastChange = useRef(0);

    useEffect(() => {
        lastChange.current = Date.now() + 240;
        setTimeout(() => { if (Date.now() > lastChange.current) { propsSearch(search); setPage(0) } }, 250)
    }, [search])

    useEffect(() => {
        if (EscKeyStroke) {
            setSearch('');
            propsSearch('');
            setPage(0);
        }
    }, [EscKeyStroke])

    return (
        <Box
            sx={{
                my: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 3,
            }}
        >
            <FormControl sx={{
                m: 1,
                width: '25ch',
            }} variant="outlined">
                <InputLabel htmlFor="outlined-adornment-search">Buscar</InputLabel>
                <OutlinedInput
                    id="outlined-adornment-search"
                    type='text'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                aria-label='search'
                                edge="end"
                            >
                                <SearchIcon />
                            </IconButton>
                        </InputAdornment>
                    }
                    label="Buscar"
                />
            </FormControl>
            <Button
                variant="outlined"
                color="secondary"
                sx={{
                    p: 2,
                    minWidth: 0,
                    '& .MuiButton-icon': { m: 0 }
                }}
                startIcon={<FilterListIcon />}
                onClick={() => setOpenDialog(!openDialog)}
            />
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
            >
                <DialogTitle>Columnas</DialogTitle>
                <DialogContent>
                    <FormGroup >
                        {columns.map(col =>
                            <FormControlLabel
                                key={'columnChecker-' + col.field}
                                disabled={['checked', 'socio', 'fullname', 'documentonumero'].includes(col.field)}
                                sx={{ display: ['checked'].includes(col.field) ? 'none' : 'flex' }}
                                control={<Checkbox
                                    defaultChecked={!(userFilter || []).includes(col.field)}
                                    onChange={(e) => {
                                        let filters = (userFilter || []);
                                        if (!e.target.checked) filters.push(col.field);
                                        else filters = filters.filter(f => f !== col.field);
                                        set(ref(rtdb, "users/" + User.uid + '/filtro'), filters);
                                    }}
                                />}
                                label={col.headerName}
                            />
                        )}
                    </FormGroup>
                </DialogContent>
            </Dialog>
        </Box>
    )
}

function strNormalize(str) {
    str = str.toLowerCase();
    str = str.replace(/á/g, 'a');
    str = str.replace(/é/g, 'e');
    str = str.replace(/í/g, 'i');
    str = str.replace(/ó/g, 'o');
    str = str.replace(/ú/g, 'u');
    str = str.replace(/ñ/g, 'n');
    str = str.replace(/,/g, ' ');
    str = str.replace(/;/g, ' ');
    str = str.replace(/\s+/g, ' ');
    str = str.trim();
    return str
}

function Component(props) {
    const { search, setSearch, User, Members, FamilyGroups, EscKeyStroke } = props;
    const [rows, setRows] = useState([]);
    const [shownCols, setShownCols] = useState([]);
    const [cols, setCols] = useState([]);
    const [checkedRows, setCheckedRows] = useState([]);
    const [sortBy, setSortBy] = useState({});
    const [page, setPage] = useState(0);

    useEffect(() => {
        props.setCheckedRows(checkedRows || []);
    }, [checkedRows])

    useEffect(() => {
        if (Array.isArray(User.filtro))
            setShownCols(User.filtro)
        else setShownCols([])
    }, [User.filtro]);

    useEffect(() => {
        setCols(columns.filter(c => !shownCols.includes(c.field)));
    }, [shownCols]);

    useEffect(() => {
        const r = [],
            normSearch = strNormalize(search).split(' ');
        Object.keys(Members).forEach((memberID) => {
            if (Members[memberID].type === 'familia') return;
            if (sortBy.field === 'grupo_familiar' && !Members[memberID].grupo_familiar) return;

            let p = true,
                m = { mid: memberID, ...Members[memberID], checked: checkedRows.includes(memberID), perm: null, permisos: null };

            if (!!Members[memberID].grupo_familiar) {
                let fg = FamilyGroups[Members[memberID].grupo_familiar || ''] || [];
                m.familyGroup = `Grupo Familiar (${fg.length}):\n${fg.map(id => '  ' + Members[id].apellido + ', ' + Members[id].nombre).join('\n')}`;
                m.debitos = [...m.debitos, ...Members[`FG-${m.grupo_familiar}`].debitos || []];
            }

            if (search.length) {
                let mtn = strNormalize(JSON.stringify(Object.values(m)));
                for (let w of normSearch)
                    if (w.length && !mtn.includes(w)) {
                        p = false;
                        break;
                    }
            }
            if (p) r.push(m);
        })
        setRows(r);
    }, [Members, checkedRows, search, sortBy])

    function ClickedCell(params) {
        if (params.field === 'checked' && !params.header) {
            if (params.row.checked) {
                setCheckedRows(checkedRows.filter(mid => mid !== params.row.mid))
            } else {
                setCheckedRows([...checkedRows, params.row.mid])
            }
        } else if (params.field === 'checked' && params.header) {
            if (checkedRows.length < rows.length) {
                let c = [];
                rows.forEach(r => c.push(r.mid));
                setCheckedRows(c);
            } else
                setCheckedRows([]);
        } else if (params.header) {
            if (params.field !== sortBy.field)
                setSortBy({
                    field: params.field,
                    ord: 'asc'
                });
            else if (sortBy.ord === 'asc')
                setSortBy({
                    field: params.field,
                    ord: 'desc'
                });
            else setSortBy({});
        } else if (params.field === 'grupo_familiar' && params.row.grupo_familiar)
            window.location.hash = `familia?${params.row.grupo_familiar}`;
        else
            window.location.hash = `socio?${params.id}`;
    }

    return (
        <Box sx={{ maxWidth: '90vw' }}>
            <GridHeader
                propsSearch={setSearch}
                preSearch={search}
                userFilter={User.filtro}
                User={User}
                setPage={setPage}
                EscKeyStroke={EscKeyStroke}
            />
            <Paper>
                <CustomDataGrid
                    rows={rows}
                    columns={cols}
                    pageSize={10}
                    onCellClick={(params) => ClickedCell(params)}
                    sortBy={sortBy}
                    statText={checkedRows.length ? (`${checkedRows.length} sel. de ${rows.length}`) : ''}
                    page={page}
                    setPage={setPage}
                    sx={{
                        border: 0,
                        cursor: 'pointer',
                        userSelect: 'none',
                        '& thead tr': {
                            backgroundColor: '#fff'
                        },
                        '& tbody tr': {
                            backgroundColor: '#fef3e7'
                        },
                        '& tbody tr:nth-of-type(odd)': {
                            backgroundColor: '#fadcb7'
                        },
                    }}
                />
            </Paper>
        </Box>
    )
}

export default Component;
